import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import EditTextDrawer from "components/ArtWork/EditTextDrawer";
import TextField from "components/TextField";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import KeyboardTabIcon from "@mui/icons-material/KeyboardTab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import Chip from "@mui/material/Chip";
import Slider from "@mui/material/Slider";
import {
  LayersContainer,
  Header,
  CloseIconStyle,
  LayersWrapper,
  Layer,
  LayerActions,
  TextLayer,
  LayerCenter,
  AlignIcons,
  UpperLayer,
  Rotate,
} from "styles/components/ArtWork/browseLibrary";

const LayersDrawer = ({
  open,
  toggle,
  canvas,
  onApply,
  printArea,
  plus,
  orderLine,
  setOrderLine,
}) => {
  const [canvasJSON, setCanvasJSON] = useState(canvas?.toJSON());
  const [editTextDrawer, setEditTextDrawer] = useState(false);
  const [activeObjectIndex, setActiveObjectIndex] = useState(0);
  const printType = orderLine?.PrintType;

  useEffect(() => {
    if (!!canvas?.getObjects()?.length)
      canvas.setActiveObject(canvas?.getObjects()[activeObjectIndex]);
    canvas.requestRenderAll();
  }, [activeObjectIndex]);

  return (
    <Drawer anchor="left" open={open} onClose={toggle}>
      <LayersContainer>
        <Header>
          <h3>Layers ({printArea?.printAreaName})</h3>
          <CloseIconStyle onClick={toggle}></CloseIconStyle>
        </Header>
        <LayersWrapper>
          {canvasJSON?.objects?.map((o, i) => {
            const widthInPixel =
              (Number(o?.width) * o?.scaleX) /
              printArea?.designerSettings?.multiplier;
            const v = printArea?.designerSettings?.width;
            const widthInInches =
              o?.width < 1
                ? 255 / printArea?.designerSettings?.multiplier
                : widthInPixel > v
                ? v
                : widthInPixel;

            const heightInPixel =
              (Number(o?.height) * o?.scaleY) /
              printArea?.designerSettings?.multiplier;
            const vh = printArea?.designerSettings?.height;
            const heightInInches =
              o?.height < 1
                ? 255 / printArea?.designerSettings?.multiplier
                : heightInPixel > vh
                ? vh
                : heightInPixel;

            const widthPercentage = parseInt(
              ((+o?.width / 255) * 100) / +widthInInches
            );

            const isCustomGraphics = o?.src?.includes("/CustomGraphics/");

            return (
              <UpperLayer
                key={`objectsLayer${i}`}
                style={{
                  borderLeftColor:
                    activeObjectIndex === i ? "#1594db" : "transparent",
                }}
              >
                <Layer
                  onClick={() => {
                    setActiveObjectIndex(i);
                  }}
                >
                  {o?.type === "image" && (
                    <img key={i} src={o?.src} width="60" />
                  )}

                  {o?.type === "i-text" && (
                    <TextLayer>
                      <TextFieldsIcon />
                    </TextLayer>
                  )}
                  <LayerCenter>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Chip label={o?.type === "image" ? "Image" : "Text"} />
                      {o?.type === "i-text" && (
                        <div
                          style={{
                            marginLeft: 10,
                            fontWeight: o?.fontWeight,
                            fontFamily: o?.fontFamily,
                            fontStyle: o?.fontStyle,
                            fontSize: 16,
                            textDecoration: `${
                              o?.underline ? "underline" : ""
                            } ${o?.overline ? "overline" : ""} ${
                              o?.linethrough ? "line-through" : ""
                            }`,
                          }}
                        >
                          {o?.text}
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: 5, display: "none" }}>
                      <span style={{ marginRight: 10 }}>
                        <b>
                          Print Quality:&nbsp;
                          {o?.type === "i-text" || isCustomGraphics ? (
                            <span style={{ color: "green" }}>Good</span>
                          ) : (
                            <span
                              style={{
                                color:
                                  widthPercentage < 20
                                    ? "red"
                                    : widthPercentage > 20 &&
                                      widthPercentage < 50
                                    ? "#f36c62"
                                    : widthPercentage > 50 &&
                                      widthPercentage < 80
                                    ? "orange"
                                    : "green",
                              }}
                            >
                              {widthPercentage < 20
                                ? "Very Poor"
                                : widthPercentage > 20 && widthPercentage < 50
                                ? "Poor"
                                : widthPercentage > 50 && widthPercentage < 80
                                ? "Average"
                                : "Good"}
                            </span>
                          )}
                        </b>
                      </span>
                    </div>

                    <div style={{ marginTop: 5 }}>
                      <span style={{ marginRight: 10 }}>
                        <b>Width:</b>&nbsp;
                        {widthInInches.toFixed(2)}"
                      </span>
                      <span>
                        <b>Height:</b>&nbsp;
                        {heightInInches.toFixed(2)}"
                      </span>
                    </div>
                  </LayerCenter>
                  <LayerActions>
                    {o?.type === "i-text" && (
                      <Tooltip title="Edit">
                        <EditIcon
                          style={{ color: "#1594db" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setEditTextDrawer({ ...o, index: i });
                          }}
                        />
                      </Tooltip>
                    )}
                    {printType !== "EMB" && o?.type !== "image" && (
                      <Tooltip title="Clone">
                        <FileCopyIcon
                          style={{ color: "#1594db" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            const data = {
                              ...canvasJSON,
                              objects: [
                                ...canvasJSON.objects,
                                { ...o, top: o?.top + 10, left: o?.left + 10 },
                              ],
                            };

                            setCanvasJSON(data);
                            onApply(data);
                            setTimeout(() => {
                              setActiveObjectIndex(
                                canvas?.toJSON()?.objects?.length - 1
                              );
                            }, 100);
                          }}
                        />
                      </Tooltip>
                    )}

                    <Tooltip title="Delete">
                      <DeleteIcon
                        style={{ color: "red" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();

                          const data = {
                            ...canvasJSON,
                            objects: canvasJSON.objects.filter(
                              (item, j) => j !== i
                            ),
                          };

                          setCanvasJSON(data);
                          onApply(data);
                          setActiveObjectIndex(0);

                          if (o?.type === "image") {
                            const printAreas = orderLine?.printAreas?.map(
                              (p) => {
                                if (!p?.selected) return p;

                                const customGraphicsdData =
                                  p?.customGraphicsdData;

                                return {
                                  ...p,
                                  customGraphicsdData:
                                    customGraphicsdData?.filter(
                                      (c) =>
                                        !o?.src?.includes(
                                          c?.fileName?.split(".")[0]
                                        )
                                    ),
                                };
                              }
                            );
                            setOrderLine({
                              ...orderLine,
                              printAreas,
                            });
                          }
                        }}
                      />
                    </Tooltip>
                  </LayerActions>
                </Layer>
                {activeObjectIndex === i && (
                  <div style={{ marginTop: 5 }}>
                    <h4 style={{ margin: "0 0 5px 0" }}>Align</h4>
                    <AlignIcons>
                      <Tooltip title="Horizontal Align">
                        <FormatAlignCenterIcon
                          onClick={() => {
                            const obj = canvas.getActiveObject();
                            obj.centerH();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", { target: obj });
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Vertical Align">
                        <VerticalAlignCenterIcon
                          onClick={() => {
                            const obj = canvas.getActiveObject();
                            obj.centerV();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", { target: obj });
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Align Top">
                        <VerticalAlignTopIcon
                          onClick={() => {
                            const obj = canvas.getActiveObject();
                            obj.set({ top: 0 });
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", { target: obj });
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Align Bottom">
                        <VerticalAlignBottomIcon
                          onClick={() => {
                            const activeObject = canvas.getActiveObject();
                            activeObject.set({
                              top:
                                canvas?.height -
                                activeObject.height * activeObject.scaleY,
                            });
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", {
                              target: activeObject,
                            });
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Align Right">
                        <KeyboardTabIcon
                          onClick={() => {
                            const activeObject = canvas.getActiveObject();
                            activeObject.set({
                              left:
                                canvas.width -
                                activeObject.width * activeObject.scaleX,
                            });
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", {
                              target: activeObject,
                            });
                          }}
                        />
                      </Tooltip>
                      <Tooltip title="Align Left">
                        <ArrowBackIcon
                          onClick={() => {
                            const activeObject = canvas.getActiveObject();
                            activeObject.set({
                              left: 0,
                            });
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", {
                              target: activeObject,
                            });
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Flip Vertical">
                        <SyncAltIcon
                          onClick={() => {
                            const activeObject = canvas.getActiveObject();
                            activeObject.toggle("flipX");
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", {
                              target: activeObject,
                            });
                          }}
                        />
                      </Tooltip>

                      <Tooltip title="Flip Horizontal">
                        <ImportExportIcon
                          onClick={() => {
                            const activeObject = canvas.getActiveObject();
                            activeObject.toggle("flipY");
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", {
                              target: activeObject,
                            });
                          }}
                        />
                      </Tooltip>
                    </AlignIcons>
                  </div>
                )}

                {o?.type === "i-text" && (
                  <div style={{ marginTop: 15 }}>
                    <h4 style={{ margin: "0 0 5px 0" }}>Font Size</h4>
                    <Rotate>
                      <Slider
                        defaultValue={o?.fontSize}
                        min={10}
                        max={90}
                        value={o?.fontSize}
                        onChange={(e, v) => {
                          const obj = canvas.getActiveObject();
                          obj.set("fontSize", v);
                          canvas.requestRenderAll();
                          const json = canvas?.toJSON();
                          setCanvasJSON(json);
                          canvas.fire("object:modified", { target: obj });
                        }}
                      />

                      <TextField
                        label="Font Size"
                        min={10}
                        max={90}
                        type="number"
                        size="small"
                        value={o?.fontSize}
                        onChange={(fontSize) => {
                          if (+fontSize <= 90) {
                            const obj = canvas.getActiveObject();
                            obj.set("fontSize", fontSize || 10);
                            canvas.requestRenderAll();
                            const json = canvas?.toJSON();
                            setCanvasJSON(json);
                            canvas.fire("object:modified", { target: obj });
                          }
                        }}
                      />
                    </Rotate>
                  </div>
                )}

                {activeObjectIndex === i && (
                  <div style={{ marginTop: 15 }}>
                    <h4 style={{ margin: "0 0 5px 0" }}>Rotate</h4>
                    <Rotate>
                      <Slider
                        defaultValue={10}
                        min={-180}
                        max={180}
                        value={o?.angle}
                        onChange={(e, v) => {
                          const obj = canvas.getActiveObject();
                          obj.rotate(v);
                          canvas.requestRenderAll();
                          const json = canvas?.toJSON();
                          setCanvasJSON(json);
                          canvas.fire("object:modified", { target: obj });
                        }}
                      />

                      <TextField
                        label="Angle"
                        type="number"
                        size="small"
                        value={o?.angle}
                        onChange={(angle) => {
                          const obj = canvas.getActiveObject();
                          obj.rotate(Number(angle));
                          canvas.requestRenderAll();
                          const json = canvas?.toJSON();
                          setCanvasJSON(json);
                          canvas.fire("object:modified", { target: obj });
                        }}
                      />
                    </Rotate>
                  </div>
                )}
              </UpperLayer>
            );
          })}
        </LayersWrapper>
      </LayersContainer>
      {!!editTextDrawer && (
        <EditTextDrawer
          plus={plus}
          open={!!editTextDrawer}
          toggle={() => setEditTextDrawer(false)}
          onDrop={(json) => {
            const data = {
              ...canvasJSON,
              objects: canvasJSON.objects?.map((o, i) => {
                if (i === json?.index) return json;
                return o;
              }),
            };
            setCanvasJSON(data);
            setEditTextDrawer(false);
            onApply(data);
          }}
          data={editTextDrawer}
        />
      )}
    </Drawer>
  );
};

export default LayersDrawer;
